<script setup>
import { computed, nextTick, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import Modal from "./Modal.vue";
import { PrimaryButton } from "@Components/Button";
import { useStore as useUserStore } from "@Stores/user";
import { useStore as useDomainStore } from "@Stores/domain";
import { logout as authLogout } from "@/auth";
import AppLink from "@Components/AppLink.vue";

const { t: __ } = useI18n();
const userStore = useUserStore();
const domainStore = useDomainStore();
const router = useRouter();

const user = computed(() => userStore.user);
const domain = computed(() => domainStore.domain);

const modal = ref();
const mode = ref("domain");

const logout = async () => {
    try {
        await authLogout();
        router.push({ name: "login" });
    } catch (e) {}
};

watch(
    [() => user.value.has_subscription, () => domain.value.is_premium],
    () => {
        if (!domain.value.is_premium) {
            mode.value = "domain";
            nextTick(() => {
                modal.value.show();
            });
        }
        if (user.value.has_subscription !== undefined && !user.value.has_subscription) {
            mode.value = "user";
            nextTick(() => {
                modal.value.show();
            });
        }
    },
    { immediate: true }
);
</script>

<template>
    <Modal ref="modal" :dismissible="false">
        <template v-if="mode === 'user'">
            <div class="p-6">
                <div class="text-lg font-medium text-center text-gray-900 dark:text-white">
                    {{ __("Vous devez souscrire à un plan premium pour continuer.") }}
                </div>
                <div class="mt-3 text-sm text-center text-gray-500 dark:text-gray-400">
                    {{ __("Souscrivez à un plan premium pour continuer.") }}
                </div>
                <div class="flex justify-center items-center mt-3">
                    <PrimaryButton :as="AppLink" to="https://studio.otomatic.ai/user/billing" target="_blank" @click="subscriptionRequiredModalRef.hide()"
                        >{{ __("Souscrire à un plan premium") }}
                    </PrimaryButton>
                </div>

                <div class="flex justify-end mt-6">
                    <button class="text-sm text-gray-500 dark:text-gray-400 dark:hover:text-gray-100 hover:text-gray-900 hover:underline" @click="logout">
                        {{ __("Déconnexion") }}
                    </button>
                </div>
            </div>
        </template>
        <template v-if="mode === 'domain'">
            <div class="p-6">
                <div class="text-lg font-medium text-center text-gray-900 dark:text-white">
                    {{ __("Votre quota de nombre de domaines a été dépassé.") }}
                </div>
                <div class="mt-3 text-sm text-center text-gray-500 dark:text-gray-400">
                    {{ __("Souscrivez à un plan premium pour augmenter votre quota.") }}
                </div>
                <div class="flex justify-center items-center mt-3">
                    <PrimaryButton :as="AppLink" to="https://studio.otomatic.ai/user/billing" target="_blank" @click="quotaExceededModalRef.hide()"
                        >{{ __("Modifier mon plan") }}
                    </PrimaryButton>
                </div>
                <div class="mt-8 text-sm text-center text-gray-500 dark:text-gray-400">
                    {{ __("Vous pouvez également supprimer un domaine pour libérer de la place.") }}
                </div>
                <div class="flex justify-center items-center mt-3">
                    <PrimaryButton :as="AppLink" to="https://studio.otomatic.ai/domains" target="_blank" @click="quotaExceededModalRef.hide()">{{
                        __("Supprimer un domaine")
                    }}</PrimaryButton>
                </div>

                <div class="flex justify-end mt-6">
                    <button class="text-sm text-gray-500 dark:text-gray-400 dark:hover:text-gray-100 hover:text-gray-900 hover:underline" @click="logout">
                        {{ __("Déconnexion") }}
                    </button>
                </div>
            </div>
        </template>
    </Modal>
</template>
